/**
 * Drives the Réciteo walkthrough: one step visible at a time, plus the two
 * in-step choices (narration style, guided vs free speech).
 *
 * Everything degrades without JS — every step is in the DOM, so a crawler
 * and a reader with scripting off get the whole story, stacked.
 */

const SAMPLES = [
  '« Je l’ai vu arriver au bord de la piste. Il faisait semblant de ne pas me regarder, et moi de ne pas le voir. »',
  '« Il la remarqua au bord de la piste, cet été-là. Il affectait l’indifférence ; elle feignit de ne pas le voir. »',
  '« Il était au bord de la piste. Il ne me regardait pas. Je ne le regardais pas non plus. C’était l’été 62. »',
];

class ReciteoWalkthrough extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-rwt-step]'));
    if (this.steps.length === 0) return;

    this.dotsHost = this.querySelector('[data-rwt-dots]');
    this.live = this.querySelector('[data-rwt-live]');
    this.restart = this.querySelector('[data-rwt-restart]');

    this.buildDots();

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);

    this.show(0, { announce: false });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  buildDots() {
    if (!this.dotsHost) return;
    this.dotsHost.replaceChildren(
      ...this.steps.map(() => {
        const dot = document.createElement('span');
        dot.className = 'rwt__dot';
        return dot;
      })
    );
  }

  onClick = (event) => {
    const next = event.target.closest('[data-rwt-next]');
    if (next) return this.show(this.index + 1);

    if (event.target.closest('[data-rwt-prev]')) return this.show(this.index - 1);
    if (event.target.closest('[data-rwt-restart]')) return this.show(0);

    const style = event.target.closest('[data-rwt-style]');
    if (style) return this.pickStyle(style);

    const mode = event.target.closest('[data-rwt-mode]');
    if (mode) return this.pickMode(mode);

    return undefined;
  };

  onKeydown = (event) => {
    if (event.target.closest('button')) return;
    if (event.key === 'ArrowRight') this.show(this.index + 1);
    if (event.key === 'ArrowLeft') this.show(this.index - 1);
  };

  show(rawIndex, { announce = true } = {}) {
    const index = Math.max(0, Math.min(rawIndex, this.steps.length - 1));
    this.index = index;

    this.steps.forEach((step, i) => {
      step.hidden = i !== index;
    });

    if (this.dotsHost) {
      Array.from(this.dotsHost.children).forEach((dot, i) => {
        dot.classList.toggle('is-done', i <= index);
        dot.classList.toggle('is-current', i === index);
      });
    }

    // Each step carries its own back button; the first step has nowhere to go.
    this.querySelectorAll('[data-rwt-prev]').forEach((button) => {
      button.hidden = index === 0;
    });

    if (this.restart) this.restart.hidden = index !== this.steps.length - 1;

    if (announce && this.live) {
      const heading = this.steps[index].querySelector('.rwt-step__heading');
      this.live.textContent = `${index + 1}/${this.steps.length} — ${heading ? heading.textContent.trim() : ''}`;
    }
  }

  pickStyle(button) {
    const group = button.closest('.rwt-styles');
    const sample = button.closest('.rwt-phone')?.querySelector('[data-rwt-sample]');
    if (!group) return;

    group.querySelectorAll('[data-rwt-style]').forEach((option) => {
      const isPicked = option === button;
      option.classList.toggle('is-active', isPicked);
      option.setAttribute('aria-checked', String(isPicked));
    });

    const text = SAMPLES[Number(button.dataset.rwtStyle)];
    if (!sample || !text) return;

    sample.textContent = text;
    // Restart the fade: the node never unmounts, so the class has to go and
    // come back across a reflow for the animation to play again.
    sample.classList.remove('is-swapping');
    void sample.offsetWidth;
    sample.classList.add('is-swapping');
  }

  pickMode(button) {
    const phone = button.closest('.rwt-phone');
    if (!phone) return;

    const wanted = button.dataset.rwtMode;

    phone.querySelectorAll('[data-rwt-mode]').forEach((option) => {
      const isPicked = option === button;
      option.classList.toggle('is-active', isPicked);
      option.setAttribute('aria-selected', String(isPicked));
    });

    phone.querySelectorAll('[data-rwt-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.rwtPanel !== wanted;
    });
  }
}

if (!customElements.get('reciteo-walkthrough')) {
  customElements.define('reciteo-walkthrough', ReciteoWalkthrough);
}
